/**
 * @module BriefingTileComparePanel
 * @purpose Per-cluster provider compare panel rendered inside an expanded BriefingTile.
 *   Owns the useProviderBriefing hook; shows generate controls, polling status, and
 *   side-by-side compare view.
 * @depends useProviderBriefing, ProviderBriefingCompare, BriefingJobStatus, react-i18next
 * @dependedOnBy BriefingTile (expanded section)
 *
 * @rules
 * - Performance: only mounts when the tile is expanded; hook fetch fires on mount
 * - State: via useProviderBriefing hook
 * - Errors: error and providerNotConfigured displayed inline
 */

import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'lucide-react'
import { useProviderBriefing } from '../../../../../hooks/use-provider-briefing'
import { ProviderBriefingCompare } from './ProviderBriefingCompare'
import { BriefingJobStatus } from './BriefingJobStatus'

interface BriefingTileComparePanelProps {
  caseId: string
  runId: string
  clusterId: string
}

const SECTION: React.CSSProperties = {
  marginTop: '12px',
  paddingTop: '12px',
  borderTop: '1px solid var(--border)',
}

const MONO: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.7rem',
}

const BTN_REGEN: React.CSSProperties = {
  ...MONO,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: '4px',
  color: 'var(--accent)',
  padding: '3px 10px',
  cursor: 'pointer',
}

const ERROR_STYLE: React.CSSProperties = {
  ...MONO,
  color: '#dc2626',
  marginTop: '6px',
}

/**
 * Expanded tile panel that loads all provider briefings and allows per-cluster regeneration.
 *
 * @behaviour Fetches all-provider briefings on mount. The regenerate button starts an async
 *   job for the selectedLabel profile; polling continues until terminal status, then refetches.
 *   providerNotConfigured shown as an inline note, not a crash.
 * @errors Inline error display for fetch/generate failures.
 */
export function BriefingTileComparePanel({
  caseId,
  runId,
  clusterId,
}: BriefingTileComparePanelProps): React.ReactElement {
  const { t } = useTranslation('analysis')

  const {
    allBriefings,
    activeJob,
    loading,
    generating,
    error,
    providerNotConfigured,
    generate,
  } = useProviderBriefing({ caseId, runId, clusterId })

  const handleRegenerate = (): void => {
    void generate({ force: true })
  }

  return (
    <div style={SECTION}>
      <div className="flex items-center gap-2" style={{ marginBottom: '8px' }}>
        <button
          type="button"
          style={BTN_REGEN}
          disabled={generating}
          aria-disabled={generating}
          onClick={handleRegenerate}
        >
          <RefreshCw className="h-3 w-3" />
          {generating
            ? t('reasoning.briefingBoard.generate.generating')
            : t('reasoning.briefingBoard.generate.force')}
        </button>
        {providerNotConfigured && (
          <span style={{ ...MONO, color: '#dc2626' }}>
            {t('reasoning.briefingBoard.generate.providerNotConfigured')}
          </span>
        )}
      </div>

      {activeJob !== null && <BriefingJobStatus job={activeJob} />}

      {error !== null && !providerNotConfigured && (
        <p style={ERROR_STYLE} role="alert">{error}</p>
      )}

      {!loading && allBriefings !== null && (
        <ProviderBriefingCompare allBriefings={allBriefings} />
      )}

      {loading && (
        <p style={{ ...MONO, color: 'var(--text-dim)' }}>
          {t('reasoning.briefingBoard.summary.loading')}
        </p>
      )}
    </div>
  )
}
